<template>
  <!-- 素材裁剪弹窗 —— 上传的视频/音频超过时长预算时弹出，
       用户框选 ≤maxDuration 的片段，浏览器内剪出新文件后 emit done。
       Teleport 到 body：盖在素材选择弹窗（z-index 90）之上。 -->
  <Teleport to="body">
    <div class="trim-overlay">
      <div class="trim-dialog" @click.stop>
        <div class="trim-header">
          <h3>{{ $t(format === 'video' ? 'seedance.trim.titleVideo' : 'seedance.trim.titleAudio') }}</h3>
          <button
            type="button"
            class="close-btn"
            :aria-label="$t('seedance.common.close')"
            :disabled="processing"
            @click="emit('cancel')"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"/>
              <line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>

        <div class="trim-body">
          <p class="trim-hint">
            {{ $t(format === 'video' ? 'seedance.trim.instructionsVideo' : 'seedance.trim.instructionsAudio', { duration: fmt(duration), maxDuration: fmt(maxDuration) }) }}
          </p>

          <video
            v-if="format === 'video'"
            ref="mediaEl"
            :src="objectUrl"
            playsinline
            class="trim-video"
          />
          <div v-else class="trim-audio-box">
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M9 18V5l12-2v13"/><circle cx="6" cy="18" r="3"/><circle cx="18" cy="16" r="3"/>
            </svg>
            <audio ref="mediaEl" :src="objectUrl" controls class="trim-audio" />
          </div>

          <!-- 剪辑时间轴：视频胶片条 / 音频波形 + 拖拽选区窗（把手调起止、按住中间整体平移） -->
          <div
            ref="stripRef"
            class="timeline"
            :class="{ disabled: processing }"
            @pointermove="onStripPointerMove"
            @pointerup="onStripPointerUp"
            @pointercancel="onStripPointerUp"
          >
            <!-- 底图：胶片帧 / 波形 -->
            <div v-if="format === 'video'" class="strip-frames">
              <img v-for="(f, i) in frames" :key="i" :src="f" alt="" draggable="false" />
            </div>
            <canvas v-else ref="waveRef" class="wave-canvas" />

            <!-- 选区外压暗 -->
            <div class="dim dim-l" :style="{ width: `${pct(start)}%` }" />
            <div class="dim dim-r" :style="{ width: `${100 - pct(end)}%` }" />

            <!-- 选区窗口：边框 + 左右把手；按住中间整体平移 -->
            <div
              class="sel-window"
              :style="{ left: `${pct(start)}%`, width: `${Math.max(0, pct(end) - pct(start))}%` }"
              @pointerdown="onStripPointerDown('move', $event)"
            >
              <div class="sel-handle sel-handle-l" @pointerdown="onStripPointerDown('start', $event)">
                <div class="sel-handle-bar" />
              </div>
              <div class="sel-handle sel-handle-r" @pointerdown="onStripPointerDown('end', $event)">
                <div class="sel-handle-bar" />
              </div>
            </div>

            <!-- 试看播放头（预览时跟随） -->
            <div ref="playheadRef" class="playhead" />
          </div>

          <div class="clip-row">
            <button
              type="button"
              class="btn ghost small"
              :disabled="processing || duration === 0"
              @click="previewClip"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><polygon points="5 3 19 12 5 21 5 3"/></svg>
              {{ $t('seedance.trim.previewClip') }}
            </button>
            <span class="clip-len">
              <span class="strong">{{ fmt(start) }}</span> –
              <span class="strong">{{ fmt(end) }}</span> ·
              <span class="len" :class="{ over: clipLen > maxDuration + 0.05 }">{{ fmt(clipLen) }}</span>
              / {{ fmt(maxDuration) }}
            </span>
          </div>
        </div>

        <div class="trim-footer">
          <button type="button" class="btn ghost" :disabled="processing" @click="emit('cancel')">
            {{ $t('seedance.common.cancel') }}
          </button>
          <button
            type="button"
            class="btn primary"
            :disabled="processing || duration === 0 || clipLen <= 0 || clipLen > maxDuration + 0.05"
            @click="handleTrim"
          >
            <span v-if="processing" class="trim-spin" />
            <svg v-else width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <circle cx="6" cy="6" r="3"/><circle cx="6" cy="18" r="3"/><line x1="20" y1="4" x2="8.12" y2="15.88"/><line x1="14.47" y1="14.48" x2="20" y2="20"/><line x1="8.12" y1="8.12" x2="12" y2="12"/>
            </svg>
            {{ processing ? $t('seedance.trim.trimming', { percent: Math.round(progress * 100) }) : $t('seedance.trim.trimUpload') }}
          </button>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import { useSnackbarStore } from '~/composables/useSnackBarStore';
import { probeBlobDuration, trimAudioFile, trimVideoFile } from '~/utils/seedanceMedia';

const props = defineProps<{
  file: File;
  format: 'video' | 'audio';
  /** 本次可用的最大片段时长（秒）= 15s − 已占用预算 */
  maxDuration: number;
}>();
const emit = defineEmits<{
  (e: 'done', trimmed: File): void;
  (e: 'cancel'): void;
}>();

const { t } = useI18n();

const objectUrl = URL.createObjectURL(props.file);
onBeforeUnmount(() => URL.revokeObjectURL(objectUrl));

const mediaEl = ref<HTMLVideoElement | HTMLAudioElement>();
const duration = ref(0);
const start = ref(0);
const end = ref(props.maxDuration);
const processing = ref(false);
const progress = ref(0);
let previewRaf: number | null = null;

onMounted(async () => {
  const d = await probeBlobDuration(props.file, props.format);
  duration.value = d;
  start.value = 0;
  end.value = Math.min(props.maxDuration, d || props.maxDuration);
  buildTimelineBase();
});

const clipLen = computed(() => Math.max(0, end.value - start.value));

/* ---------- 时间轴底图：视频均匀抽 10 帧胶片条 / 音频解码画波形 ---------- */

const stripRef = ref<HTMLDivElement>();
const waveRef = ref<HTMLCanvasElement>();
const playheadRef = ref<HTMLDivElement>();
const frames = ref<string[]>([]);
let timelineAlive = true;
onBeforeUnmount(() => { timelineAlive = false; });

/** 视频均匀抽 N 帧生成胶片条（canvas 2x 显示高度保证清晰） */
async function extractFilmstrip(url: string, dur: number, count: number): Promise<string[]> {
  const video = document.createElement('video');
  video.src = url;
  video.muted = true;
  video.playsInline = true;
  video.preload = 'auto';
  await new Promise<void>((resolve, reject) => {
    video.onloadedmetadata = () => resolve();
    video.onerror = () => reject(new Error('Failed to load video'));
  });
  const targetH = 112; // 2x 显示高度
  const ratio = (video.videoWidth || 16) / (video.videoHeight || 9);
  const canvas = document.createElement('canvas');
  canvas.height = targetH;
  canvas.width = Math.max(1, Math.round(targetH * ratio));
  const ctx = canvas.getContext('2d');
  if (!ctx) return [];
  const out: string[] = [];
  for (let i = 0; i < count; i++) {
    const t = ((i + 0.5) / count) * dur;
    await new Promise<void>((resolve) => {
      video.onseeked = () => resolve();
      video.currentTime = Math.min(t, Math.max(0, dur - 0.05));
    });
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    out.push(canvas.toDataURL('image/jpeg', 0.6));
  }
  video.removeAttribute('src');
  video.load();
  return out;
}

/** 音频解码取峰值画波形 */
async function drawWaveform(blob: Blob, canvas: HTMLCanvasElement): Promise<void> {
  const ctx2d = canvas.getContext('2d');
  if (!ctx2d) return;
  const actx = new AudioContext();
  try {
    const decoded = await actx.decodeAudioData(await blob.arrayBuffer());
    const data = decoded.getChannelData(0);
    const { width, height } = canvas;
    const buckets = Math.floor(width / 3);
    const per = Math.floor(data.length / buckets) || 1;
    ctx2d.clearRect(0, 0, width, height);
    ctx2d.fillStyle = 'rgba(255,255,255,0.55)';
    for (let i = 0; i < buckets; i++) {
      let peak = 0;
      const base = i * per;
      // 稀疏采样够画波形了，全量太慢
      for (let j = 0; j < per; j += 32) {
        const v = Math.abs(data[base + j] ?? 0);
        if (v > peak) peak = v;
      }
      const h = Math.max(2, peak * height * 0.9);
      ctx2d.fillRect(i * 3, (height - h) / 2, 2, h);
    }
  } finally {
    await actx.close().catch(() => {});
  }
}

function buildTimelineBase() {
  if (duration.value <= 0) return;
  if (props.format === 'video') {
    extractFilmstrip(objectUrl, duration.value, 10)
      .then((f) => { if (timelineAlive) frames.value = f; })
      .catch(() => {});
    return;
  }
  const canvas = waveRef.value;
  if (canvas) {
    canvas.width = canvas.clientWidth * 2;
    canvas.height = canvas.clientHeight * 2;
    drawWaveform(props.file, canvas).catch(() => {});
  }
}

/* ---------- 选区拖拽：左右把手调起止、按住中间整体平移 ---------- */

const pct = (v: number) => (duration.value > 0 ? (v / duration.value) * 100 : 0);

type DragMode = 'start' | 'end' | 'move';
// move 模式记录按下点距选区起点的秒数，平移时保持窗口长度
let drag: { mode: DragMode; grabOffset: number } | null = null;

const secAt = (clientX: number): number => {
  const rect = stripRef.value?.getBoundingClientRect();
  if (!rect || duration.value <= 0) return 0;
  const ratio = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
  return ratio * duration.value;
};

const onStripPointerDown = (mode: DragMode, e: PointerEvent) => {
  if (processing.value) return;
  e.preventDefault();
  e.stopPropagation();
  (e.target as HTMLElement).setPointerCapture(e.pointerId);
  drag = {
    mode,
    grabOffset: mode === 'move' ? secAt(e.clientX) - start.value : 0,
  };
};

const onStripPointerMove = (e: PointerEvent) => {
  if (!drag || processing.value) return;
  const sec = secAt(e.clientX);
  if (drag.mode === 'start') {
    // 左把手：不小于 0、不越过右端 0.5s、窗口不超 maxDuration
    start.value = Math.min(Math.max(0, Math.max(sec, end.value - props.maxDuration)), end.value - 0.5);
  } else if (drag.mode === 'end') {
    end.value = Math.max(
      Math.min(Math.min(sec, start.value + props.maxDuration), duration.value),
      start.value + 0.5
    );
  } else {
    // 整体平移：保持窗口长度
    const len = end.value - start.value;
    const s = Math.max(0, Math.min(sec - drag.grabOffset, duration.value - len));
    start.value = s;
    end.value = s + len;
  }
};

const onStripPointerUp = (e: PointerEvent) => {
  drag = null;
  (e.target as HTMLElement).releasePointerCapture?.(e.pointerId);
};

// 试听/试看选中的片段，播放头跟随
const previewClip = () => {
  const el = mediaEl.value;
  if (!el || duration.value <= 0) return;
  if (previewRaf !== null) cancelAnimationFrame(previewRaf);
  el.currentTime = start.value;
  el.play().catch(() => {});
  const head = playheadRef.value;
  const tick = () => {
    if (head) {
      head.style.display = 'block';
      head.style.left = `${(el.currentTime / duration.value) * 100}%`;
    }
    if (el.currentTime >= end.value || el.ended) {
      el.pause();
      if (head) head.style.display = 'none';
      previewRaf = null;
      return;
    }
    previewRaf = requestAnimationFrame(tick);
  };
  previewRaf = requestAnimationFrame(tick);
};
onBeforeUnmount(() => {
  if (previewRaf !== null) cancelAnimationFrame(previewRaf);
});

const handleTrim = async () => {
  processing.value = true;
  progress.value = 0;
  try {
    mediaEl.value?.pause();
    const onProgress = (r: number) => { progress.value = r; };
    const trimmed =
      props.format === 'audio'
        ? await trimAudioFile(props.file, start.value, end.value, props.file.name, onProgress)
        : await trimVideoFile(props.file, start.value, end.value, props.file.name, onProgress);
    emit('done', trimmed);
  } catch (err: any) {
    useSnackbarStore().showError(err?.message || t('seedance.trim.toastFailed'));
    processing.value = false;
  }
};

const fmt = (v: number) => `${v.toFixed(1)}s`;
</script>

<style scoped>
.trim-overlay {
  position: fixed;
  inset: 0;
  z-index: 120;
  background: rgba(0, 0, 0, 0.7);
  backdrop-filter: blur(6px);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
}
.trim-dialog {
  width: 100%;
  max-width: 520px;
  display: flex;
  flex-direction: column;
  background: #111;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 1.25rem;
  overflow: hidden;
}
.trim-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.85rem 1.25rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
}
.trim-header h3 {
  color: white;
  font-size: 1rem;
  font-weight: 700;
  margin: 0;
}
.close-btn {
  background: transparent;
  border: none;
  color: rgba(255, 255, 255, 0.5);
  cursor: pointer;
  padding: 0.25rem;
  display: flex;
}
.close-btn:hover { color: white; }
.close-btn:disabled { opacity: 0.4; cursor: not-allowed; }
.trim-body { padding: 1rem 1.25rem; }
.trim-hint {
  margin: 0 0 0.75rem;
  font-size: 0.75rem;
  line-height: 1.5;
  color: rgba(255, 255, 255, 0.55);
}
.trim-hint .strong { color: white; }
.trim-hint .accent { color: #d946ef; }
.trim-video {
  width: 100%;
  max-height: 40vh;
  margin-bottom: 0.75rem;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 0.75rem;
  background: black;
}
.trim-audio-box {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  margin-bottom: 0.75rem;
  padding: 1.25rem 0.75rem;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 0.75rem;
  background: rgba(255, 255, 255, 0.04);
  color: rgba(255, 255, 255, 0.45);
}
.trim-audio { height: 36px; max-width: 70%; }
/* 剪辑时间轴 */
.timeline {
  position: relative;
  height: 3.5rem;
  width: 100%;
  user-select: none;
  overflow: hidden;
  border-radius: 0.5rem;
  background: rgba(0, 0, 0, 0.6);
  touch-action: none;
}
.timeline.disabled { pointer-events: none; opacity: 0.6; }
.strip-frames { display: flex; height: 100%; width: 100%; }
.strip-frames img {
  height: 100%;
  min-width: 0;
  flex: 1;
  object-fit: cover;
  pointer-events: none;
}
.wave-canvas { width: 100%; height: 100%; display: block; }
/* 选区外压暗 */
.dim {
  pointer-events: none;
  position: absolute;
  top: 0; bottom: 0;
  background: rgba(0, 0, 0, 0.6);
}
.dim-l { left: 0; }
.dim-r { right: 0; }
/* 选区窗口 + 左右把手 */
.sel-window {
  position: absolute;
  top: 0; bottom: 0;
  border-top: 3px solid #d946ef;
  border-bottom: 3px solid #d946ef;
  border-radius: 0.375rem;
  cursor: grab;
}
.sel-window:active { cursor: grabbing; }
.sel-handle {
  position: absolute;
  top: 0; bottom: 0;
  width: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #d946ef;
  cursor: ew-resize;
  touch-action: none;
}
.sel-handle-l { left: -8px; border-radius: 0.375rem 0 0 0.375rem; }
.sel-handle-r { right: -8px; border-radius: 0 0.375rem 0.375rem 0; }
.sel-handle-bar {
  width: 4px; height: 20px;
  border-radius: 999px;
  background: white;
  pointer-events: none;
}
/* 试看播放头 */
.playhead {
  pointer-events: none;
  position: absolute;
  top: 0; bottom: 0;
  width: 2px;
  background: white;
  display: none;
}
.clip-row {
  margin-top: 0.75rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.clip-len { font-size: 0.75rem; color: rgba(255, 255, 255, 0.55); }
.clip-len .strong { color: white; font-weight: 500; }
.clip-len .len { color: #d946ef; font-weight: 500; }
.clip-len .len.over { color: #f87171; }
.trim-footer {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0.5rem;
  padding: 0.85rem 1.25rem;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
  background: rgba(255, 255, 255, 0.02);
}
.btn {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.5rem 1rem;
  border-radius: 0.5rem;
  border: none;
  cursor: pointer;
  font-size: 0.85rem;
  font-weight: 500;
}
.btn.small { padding: 0.35rem 0.75rem; font-size: 0.75rem; }
.btn.ghost {
  background: transparent;
  color: rgba(255, 255, 255, 0.75);
  border: 1px solid rgba(255, 255, 255, 0.12);
}
.btn.ghost:hover:not(:disabled) { color: white; background: rgba(255, 255, 255, 0.08); }
.btn.primary {
  background: linear-gradient(90deg, #d946ef, #f43f5e);
  color: white;
}
.btn:disabled { opacity: 0.4; cursor: not-allowed; }
.trim-spin {
  width: 14px;
  height: 14px;
  border-radius: 50%;
  border: 2px solid rgba(255, 255, 255, 0.35);
  border-top-color: white;
  animation: trim-spin 1s linear infinite;
  display: inline-block;
}
@keyframes trim-spin { to { transform: rotate(360deg); } }
</style>
